/**
 * Manipulation du préfixe de locale dans un pathname.
 *
 * Les URLs de pages portent la locale (`/fr/cart`), alors que les policies de
 * `route-policy.ts` et les patterns Next sont déclarés sans préfixe (`/cart`).
 * Ces helpers sont la seule traduction entre les deux mondes — les garder purs
 * et sans dépendance permet de les appeler depuis le middleware comme depuis le
 * gating.
 *
 * Exemple concret : `splitLocalePath('/fr/cart')` → `{ locale: 'fr', path: '/cart' }`,
 * consommé par `checkRouteAccess` pour retrouver la policy `/cart`.
 */

import { locales, type Locale } from './config';

/** Pathnames qui ne doivent JAMAIS recevoir de préfixe de locale. */
const NON_LOCALIZABLE_PREFIXES = ['/api/', '/_next/'] as const;
const NON_LOCALIZABLE_PATHS = ['/api', '/sitemap.xml', '/robots.txt'] as const;
const FILE_EXTENSION = /\.[^/]+$/;

function isSupportedLocale(segment: string): segment is Locale {
    return (locales as readonly string[]).includes(segment);
}

/**
 * Sépare le préfixe de locale du reste du pathname. Seul le PREMIER segment est
 * inspecté, de sorte qu'un slug homonyme d'une locale (`/dmcs/it`) survive.
 *
 * `'/fr/cart'` → `{ locale: 'fr', path: '/cart' }` · `'/fr'` → `{ locale: 'fr', path: '/' }`
 * `'/cart'` → `{ locale: null, path: '/cart' }`
 */
export function splitLocalePath(pathname: string): { locale: Locale | null; path: string } {
    const [, first = '', ...rest] = pathname.split('/');
    if (!isSupportedLocale(first)) return { locale: null, path: pathname };
    return { locale: first, path: rest.length > 0 ? `/${rest.join('/')}` : '/' };
}

/** `('fr', '/cart')` → `'/fr/cart'` · `('fr', '/')` → `'/fr'`. */
export function withLocalePrefix(locale: Locale, path: string): string {
    return path === '/' ? `/${locale}` : `/${locale}${path}`;
}

/**
 * `true` pour les surfaces servies hors du segment `[locale]` : routes API,
 * internals Next, `sitemap.xml`, `robots.txt` et tout asset `public/`.
 *
 * C'est le garde-fou le plus critique de la migration : sans lui, le middleware
 * next-intl redirigerait `/api/dmcs/foo` vers `/en/api/dmcs/foo` (404 sur les 26
 * routes API) et `/home_cover.jpg` vers `/en/home_cover.jpg` (404 sur toutes les
 * images du site).
 */
export function isNonLocalizable(pathname: string): boolean {
    return (
        NON_LOCALIZABLE_PATHS.includes(pathname as (typeof NON_LOCALIZABLE_PATHS)[number]) ||
        NON_LOCALIZABLE_PREFIXES.some((prefix) => pathname.startsWith(prefix)) ||
        FILE_EXTENSION.test(pathname)
    );
}
